import apiEndpoint from '../../../config/data';
import { showToast } from '@spotoninc/nexus-react';
import { getAuthHeader } from '../../../../utils/tokenService';
// Heap Analytics tracking functions
// Note: These functions handle Heap availability checks internally via heapAnalytics.js
// No need to check Heap availability at this level - the underlying trackEvent function
// handles all availability checks, error handling, and graceful degradation
import {
  trackTaskStatusChanged,
  trackTaskFeedback,
} from '../../../../utils/analytics/taskTracking';

/**
 * Task Drawer Service
 * Contains API calls related to task drawer operations
 */
class TaskDrawerService {
  /**
   * Get authorization headers for API calls
   * @returns {Object} Headers object with authorization
   */
  getAuthHeaders() {
    const authHeader = getAuthHeader();
    return authHeader ? { Authorization: authHeader } : {};
  }

  /**
   * Modify task status
   * @param {Object} task - Task object
   * @param {string} action - Action to perform
   * @param {string} status - New status
   * @param {string} oldStatus - Previous status (optional, defaults to task.status)
   * @returns {Promise<void>} Promise that resolves when task status is updated
   */
  async modifyTaskStatus(task, action, status, oldStatus = null) {
    try {
      const taskData = new FormData();
      taskData.append('version', task?.version || '1');
      taskData.append('taskId', task.id);
      taskData.append('action', action);
      taskData.append('status', status);
      if (task?.feedbackSentiment) {
        taskData.append('feedback_sentiment', task.feedbackSentiment);
      }
      if (task?.comments) {
        taskData.append('feedback', task.comments);
      }
      const response = await fetch(apiEndpoint + '/api/v2/modify_task/', {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
        body: taskData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();

      // Track task status change in Heap
      // Use the explicitly passed oldStatus, or fall back to task.status
      trackTaskStatusChanged({
        taskId: task.id,
        taskTitle: task.task_title || task.taskTitle || '',
        oldStatus: oldStatus || task.status || '',
        newStatus: status,
      });

      // Note: We do NOT track comments here because:
      // 1. This function is for status changes, not for adding regular task comments
      // 2. When completing with feedback, comments are tracked as part of trackTaskFeedback below
      // 3. Regular task comments are tracked in taskForm.service.js modifyTask() function
      // This prevents duplicate TASK_COMMENTED events

      // Track feedback only when completing a task with feedback
      // This is separate from setFeedbackValue which handles feedback on already-completed tasks
      // Only track if status is being changed to 'completed' to avoid duplicate tracking
      const isCompletingTask = status === 'completed';
      if (
        isCompletingTask &&
        task?.feedbackSentiment !== undefined &&
        task?.feedbackSentiment !== null
      ) {
        trackTaskFeedback({
          taskId: task.id,
          taskTitle: task.task_title || task.taskTitle || '',
          taskStatus: status,
          feedbackSentiment: task.feedbackSentiment,
          feedbackComments: task.comments || '',
        });
      }

      showToast({
        priority: 'success',
        content: 'Task status updated successfully',
        autoClose: 6000,
      });
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating task status:', error);

      showToast({
        priority: 'error',
        content:
          'Failed to update task status: ' + (error.message || 'Unknown error'),
        autoClose: 6000,
      });

      throw error;
    }
  }

  async changeTaskOwner(taskId, newOwnerId) {
    try {
      const taskData = new FormData();
      taskData.append('taskId', taskId);
      taskData.append('assigned_to', newOwnerId);
      taskData.append('version', '2');
      taskData.append('action', 'modify');
      const response = await fetch(apiEndpoint + '/api/v2/modify_task/', {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
        body: taskData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      return Promise.resolve();
    } catch (error) {
      console.error('Error changing task owner:', error);
      throw error;
    }
  }

  async setFeedbackValue({
    taskId,
    feedbackSentiment,
    comments = '',
    taskTitle = '',
    taskStatus = '',
  }) {
    try {
      const taskData = new FormData();
      taskData.append('taskId', taskId);
      taskData.append('feedback_sentiment', feedbackSentiment);
      taskData.append('feedback', comments);
      taskData.append('version', '2');
      taskData.append('action', 'modify');
      const response = await fetch(apiEndpoint + '/api/v2/modify_task/', {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
        body: taskData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Track feedback submission in Heap
      if (feedbackSentiment !== undefined && feedbackSentiment !== null) {
        trackTaskFeedback({
          taskId,
          taskTitle,
          taskStatus,
          feedbackSentiment,
          feedbackComments: comments,
        });
      }

      return Promise.resolve();
    } catch (error) {
      console.error('Error setting feedback value:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const taskDrawerService = new TaskDrawerService();
export default taskDrawerService;
